// ===========================================
// Teacher Workload Table Component
// ===========================================
// Sortable table showing teacher workload metrics with color-coded scores

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
} from '@mui/material';

import { TeacherWorkload } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface TeacherWorkloadTableProps {
  data: TeacherWorkload[];
  loading?: boolean;
}

type SortField = 'teacherName' | 'totalLessons' | 'totalHours' | 'averageClassSize' | 'replacementCount' | 'workloadScore';
type SortDirection = 'asc' | 'desc';

// ===========================================
// HELPERS
// ===========================================

// Get workload status color
const getWorkloadColor = (score: number): 'success' | 'warning' | 'error' | 'default' => {
  if (score > 80) return 'error'; // Overloaded
  if (score >= 60) return 'success'; // Balanced
  if (score >= 40) return 'warning'; // Underutilized
  return 'default'; // Very underutilized
};

// Get workload status label
const getWorkloadLabel = (score: number): string => {
  if (score > 80) return 'Overloaded';
  if (score >= 60) return 'Balanced';
  if (score >= 40) return 'Light';
  return 'Very Light';
};

// Sort data
const sortData = (data: TeacherWorkload[], field: SortField, direction: SortDirection): TeacherWorkload[] => {
  return [...data].sort((a, b) => {
    let aValue = a[field];
    let bValue = b[field];

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
};

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherWorkloadTable({ data, loading }: TeacherWorkloadTableProps) {
  const [sortField, setSortField] = useState<SortField>('workloadScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Teacher Workload Distribution
          </Typography>
          <Alert severity="info">
            No teacher workload data available for the selected period.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = sortData(data, sortField, sortDirection);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Teacher Workload Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          Workload balance across teaching staff
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'teacherName'}
                    direction={sortField === 'teacherName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('teacherName')}
                  >
                    Teacher
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'totalLessons'}
                    direction={sortField === 'totalLessons' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalLessons')}
                  >
                    Lessons
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'totalHours'}
                    direction={sortField === 'totalHours' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalHours')}
                  >
                    Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'averageClassSize'}
                    direction={sortField === 'averageClassSize' ? sortDirection : 'asc'}
                    onClick={() => handleSort('averageClassSize')}
                  >
                    Avg Class Size
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'replacementCount'}
                    direction={sortField === 'replacementCount' ? sortDirection : 'asc'}
                    onClick={() => handleSort('replacementCount')}
                  >
                    Replacements
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'workloadScore'}
                    direction={sortField === 'workloadScore' ? sortDirection : 'asc'}
                    onClick={() => handleSort('workloadScore')}
                  >
                    Workload Score
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((teacher) => (
                <TableRow key={teacher.teacherId} hover>
                  <TableCell>{teacher.teacherName}</TableCell>
                  <TableCell align="right">{teacher.totalLessons}</TableCell>
                  <TableCell align="right">{teacher.totalHours.toFixed(1)}</TableCell>
                  <TableCell align="right">{teacher.averageClassSize.toFixed(1)}</TableCell>
                  <TableCell align="right">{teacher.replacementCount}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${teacher.workloadScore} - ${getWorkloadLabel(teacher.workloadScore)}`}
                      color={getWorkloadColor(teacher.workloadScore)}
                      size="small"
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
